import Logo from "~/components/Logo"
import {
  articleBodyStyle,
  articleCompareImages,
  articleFaqBodyStyle,
  articleImageStyle,
  bold,
  faqGrid,
  faqH3Style,
  figCaptionStyle,
  h1Style,
  h1Wrap,
  h2Style,
  h3Style,
  headerTextStyle,
  imagePreviewBodyStyle,
  infoWrap,
  orderedList,
  stepSectionStyle,
  unorderedList,
} from "./article.css"
import { MetaFunction } from "@remix-run/node"
import StickyHeader from "~/components/StickyHeader"
import { page } from "~/shared/styles.css"
import LinkButton from "~/components/LinkButton"
import Link from "~/components/Link"
import Footer from "~/components/Footer"
import { golden, vars } from "~/components/styles/theme.css"
import {
  Bold,
  FAQGrid,
  FAQItem,
  Figure,
  H2,
  H3,
  Image,
  ImagePair,
  P,
  Page,
  Section,
} from "~/components/articleComponents"

const TITLE = "Cottage Gardens: How to Create a Timeless English Garden"
const DESCRIPTION =
  "Discover how to design a classic cottage garden that blends romance, abundance, and natural beauty. Create an enchanting outdoor space."
const URL = "https://rescapeai.com/garden-design-guide/cottage/materials-and-plants"
const IMAGE = "https://rescapeai.com/guide/cottage-garden-bench.webp"

export const meta: MetaFunction = () => {
  return [
    {
      title: TITLE,
    },
    {
      name: "description",
      content: DESCRIPTION,
    },
    {
      property: "og:title",
      content: TITLE,
    },
    {
      property: "og:description",
      content: DESCRIPTION,
    },
    {
      property: "og:image",
      content: IMAGE,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: URL,
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: TITLE,
        description: DESCRIPTION,
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2025-10-22",
        dateModified: "2025-10-22",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: IMAGE,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": URL,
        },
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/articles/cottage-garden-bench.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>
            Cottage Gardens: A Guide to Creating Charm and Abundance
          </h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />

      <Page>
        <Section>
          <P>
            Cottage gardens celebrate romance, informality, and abundance. Cottage gardens
            overflow with colour, texture, and life. They capture the nostalgic beauty of
            old English countryside homes—where flowers tumble over paths, herbs mingle
            with blooms, and wildlife finds a welcoming refuge.
          </P>
          <ImagePair
            one={{
              src: "/guide/greenhouse-cottage-garden-early-fall-view-WM-SQ.webp",
              alt: "Blooming flowers enlighten the garden",
            }}
            two={{
              src: "/guide/garden-party-ready-cottage-garden-with-small-cottage-garden-ideas-including-dining-furniture-positioned-among-abundant-flowering-plants-and-pathways.webp",
              alt: "A bench in sunlight down a cottage path",
            }}
          />
          <P>
            Rooted in practicality and charm, cottage gardens mix beauty and
            utility—combining ornamentals, herbs, and edibles in joyful profusion. Whether
            you’re working with a large country plot or a small urban patch, this style
            can bring warmth and timeless personality to any outdoor space.
          </P>
          <H2>Informal Structure and Natural Flow</H2>
          <P>
            Cottage gardens thrive on an effortless look, though their magic lies in
            carefully orchestrated layers. Paths curve gently rather than running
            straight, and plants spill freely over edges to blur the boundaries between
            order and wilderness.
          </P>
          <Figure
            src="/guide/e2d2405c-c0a2-4179-906a-6674a8438c231.webp"
            caption="Garden path idea generated on Rescape AI. 30% Creativity setting"
          />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "1rem",
              marginBottom: "2rem",
            }}
          >
            <LinkButton url="/hello/signup">Design your cottage garden</LinkButton>
          </div>
          <P>
            Start by sketching a loose structure. Winding gravel paths, low stone walls,
            or reclaimed brick edging help define sections without making them rigid. The
            goal is not perfection but *character*.
          </P>
          <P>
            Fences or hedges (like hawthorn or lavender) give enclosure and intimacy,
            creating a sense of discovery as you move through the space.
          </P>
        </Section>
        <Section>
          <H3>Design Tips:</H3>
          <ul className={unorderedList}>
            <li>Let plants mingle—avoid single-species beds.</li>
            <li>Combine tall and short varieties for depth and movement.</li>
            <li>
              Include focal points like a rustic gate, climbing rose arch, or weathered
              bench.
            </li>
          </ul>
          <H2>Stone, Timber, and Timeworn Materials</H2>
          <Image
            src="/guide/stone-wall.webp"
            alt="A stone brick wall made with reclaimed stone and boulders"
          />
          <P>
            Cottage gardens feel timeless because they use materials that age gracefully.
            Reclaimed stone, old bricks, and weathered wood all contribute to the garden’s
            story.
          </P>
          <P>
            Paths made from irregular flagstones or gravel soften with moss and
            self-seeded plants over time. Timber pergolas and picket fences invite
            climbers like honeysuckle or clematis, creating vertical interest and gentle
            scent.
          </P>
          <P>
            Avoid sleek, modern finishes—imperfections are part of the charm. Over time,
            lichen, rust, and patina enhance the garden’s authenticity, embodying the
            lived-in beauty that defines this style.
          </P>
          <H2>Abundance in Planting Design</H2>
          <Image
            src="/guide/8CoqMwAPBA9y6giu2jpu7J.webp"
            alt="Flowerbed full of blooming plants"
          />
          <P>
            At the heart of every cottage garden is generous, layered planting. Flowers,
            herbs, vegetables, and fruit plants all coexist in delightful disorder. Colour
            and texture matter more than precision—plants are chosen for personality and
            scent rather than symmetry.
          </P>
          <P>
            Think of your planting as a living tapestry: tall spires, soft mounds, airy
            fillers, and ground-hugging creepers. Mix perennials, self-seeders, and
            annuals for ever-changing interest.
          </P>
          <H3>Planting Tips:</H3>
          <ul className={unorderedList}>
            <li>Plant in groups of odd numbers for a natural look.</li>
            <li>Blend colours—pastels for romance, or jewel tones for richness.</li>
            <li>Allow some plants to self-seed for a spontaneous effect.</li>
          </ul>
          <H2>Essential Cottage Garden Plants</H2>
          <P>
            Cottage gardens draw their charm from a rich, evolving mix of plants that
            invite bees, butterflies, and birds.
          </P>
          <H3>Roses</H3>
          <Image src="/guide/roses.webp" alt="Red Roses" />
          <P>
            The quintessential cottage flower. Choose fragrant varieties like Rosa
            ‘Gertrude Jekyll’ or Rosa ‘New Dawn’. Train climbing roses over arches or
            walls for classic English character.
          </P>
          <H3>Foxgloves</H3>
          <Image src="/guide/common-foxglove.webp" alt="Common Foxglove flower" />
          <P>
            Tall, stately spires that add height and drama. Their speckled bells attract
            pollinators and bring old-world romance to shady spots.
          </P>
          <H3>Lavender</H3>
          <Image
            src="/guide/lavender.webp"
            alt="Close up of the delicate lavender flowers"
          />
          <P>
            Perfect for edging paths, lavender offers scent, structure, and year-round
            appeal. Its silvery foliage complements vibrant flowers and deters pests
            naturally.
          </P>
          <H3>Delphiniums</H3>
          <Image src="/guide/delphinium.webp" alt="Delphenium flowers" />
          <P>
            These elegant towers of blue, purple, or white flowers make a stunning summer
            statement. Stake them early to prevent wind damage.
          </P>
          <H3>Geraniums (Hardy Cranesbills)</H3>
          <Image src="/guide/geranium.webp" alt="Geranium flowers" />
          <P>
            Reliable, long-flowering, and easy to grow. Their mounding habit fills gaps
            beautifully, keeping weeds at bay while softening borders.
          </P>
          <H3>Herbs and Edibles</H3>
          <Image src="/guide/rosemary.webp" alt="Rosemary with blue white blooms" />
          <P>
            Traditional cottage gardens blend beauty with utility. Include thyme, sage,
            chives, and mint among your flowers. Add rhubarb, strawberries, or currant
            bushes for edible charm.
          </P>
        </Section>
        <Section>
          <H2>Seasonal Colour and Year-Round Interest</H2>
          <P>
            Cottage gardens are never static. They shift gently with the seasons, ensuring
            that every corner has something to offer—whether it’s spring bulbs, summer
            roses, or the golden tones of autumn seed heads.
          </P>
          <Bold>Recommended seasonal highlights:</Bold>
          <div className={articleCompareImages}>
            <FAQItem
              smaller
              title="Spring: Tulips, forget-me-nots, primroses"
              body={
                <img
                  className={articleImageStyle}
                  style={{ marginTop: 0 }}
                  src="/guide/tulips.webp"
                  alt="Burgundy Tulips"
                />
              }
            />
            <FAQItem
              smaller
              title="Summer: Roses, delphiniums, hollyhocks, lavender"
              body={
                <img
                  className={articleImageStyle}
                  style={{ marginTop: 0 }}
                  src="/guide/hollyhock.webp"
                  alt="Hollyhock"
                />
              }
            />
          </div>
          <div className={articleCompareImages} style={{ marginTop: `${golden}rem` }}>
            <FAQItem
              smaller
              title="Autumn: Dahlias, asters, sedums"
              body={
                <img
                  className={articleImageStyle}
                  style={{ marginTop: 0 }}
                  src="/guide/asters.webp"
                  alt="Purple & White Asters"
                />
              }
            />
            <FAQItem
              smaller
              title="Winter: Hellebores, evergreen herbs, snowdrops"
              body={
                <img
                  className={articleImageStyle}
                  style={{ marginTop: 0 }}
                  src="/guide/snowdrops.webp"
                  alt="Purple tulips"
                />
              }
            />
          </div>
        </Section>
        <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
          <LinkButton url="/hello/signup">Join Rescape AI</LinkButton>
        </div>
        <Footer showLinks={false} />
      </Page>
    </>
  )
}
export default Index
